import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { hrDigitalEnablementAPI } from '../utils/api';
import { isAdmin } from '../utils/auth';
import './DashboardPage.css';

const notifyHrDataChanged = () => globalThis.dispatchEvent(new Event('hrDataChanged'));

const getFiscalYearStartYear = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const month = d.getMonth();
  const year = d.getFullYear();
  return month >= 9 ? year : year - 1;
};

const formatYearLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`;

const getStoredHrYear = () => {
  const current = getFiscalYearStartYear(new Date());
  try {
    const raw = localStorage.getItem('hrSelectedYear');
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : current;
  } catch {
    return current;
  }
};

const getCompletionColor = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return 'secondary';
  if (value >= 75) return 'success';
  if (value >= 40) return 'warning';
  return 'danger';
};

const formatCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return '0%';
  return `${value}%`;
};

const normalizeCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const MONTH_COLS = [
  { key: 'nov_25', label: 'Nov-25' },
  { key: 'dec_25', label: 'Dec-25' },
  { key: 'jan_26', label: 'Jan-26' },
  { key: 'feb_26', label: 'Feb-26' },
  { key: 'mar_26', label: 'Mar-26' },
  { key: 'apr_26', label: 'Apr-26' },
  { key: 'may_26', label: 'May-26' },
  { key: 'jun_26', label: 'Jun-26' },
  { key: 'jul_26', label: 'Jul-26' },
  { key: 'aug_26', label: 'Aug-26' },
  { key: 'sep_26', label: 'Sep-26' },
  { key: 'oct_26', label: 'Oct-26' },
];

const EMPTY_FORM = {
  actual_start_date: '',
  actual_end_date: '',
  completion_pct: '',
  responsibility: '',
  nov_25: '', dec_25: '', jan_26: '', feb_26: '', mar_26: '', apr_26: '',
  may_26: '', jun_26: '', jul_26: '', aug_26: '', sep_26: '', oct_26: '',
};

const COMMON_GUIDELINES = " - To be seen in the system.\n - Sample reports from various processes to be generated and seen as evidence.\n - Random feedback in a structured way from users (across functions) to be availed on quarterly basis and recorded.\n\n- 100% Marks for all processes which are system enabled & running\n- 0% Marks if module is not enabled or utilized";

const COMMON_AUDITOR_DETAILS = "a) Evidence of all 10 HR processes to be provided that the same are system enabled.\n\nb) Evidence of random feedback shall be obtained discreetly to check the same is in place in a structured manner across businesses.";

export const INIT_PARAMETERS = [
  { id: 'organization-management', name: 'Organization Management (including business partners)', subParameters: [{ id: 'organization-management-1', label: 'DE-01', marks: 40, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'recruiting-onboarding', name: 'Recruiting & Onboarding', subParameters: [{ id: 'recruiting-onboarding-1', label: 'DE-02', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'performance-management-system', name: 'Performance Management System', subParameters: [{ id: 'performance-management-system-1', label: 'DE-03', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'talent-management-process', name: 'Talent Management Process (Mandatory inclusion of mobility / rotation platform)', subParameters: [{ id: 'talent-management-process-1', label: 'DE-04', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'learning-management', name: 'Learning Management', subParameters: [{ id: 'learning-management-1', label: 'DE-05', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'reward-recognition', name: 'Reward & Recognition (Including compensation, incentives, bonus, stock options)', subParameters: [{ id: 'reward-recognition-1', label: 'DE-06', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'time-leave-management', name: 'Time & Leave management system', subParameters: [{ id: 'time-leave-management-1', label: 'DE-07', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'employee-manager-self-service', name: 'Employee & Manager self service portals', subParameters: [{ id: 'employee-manager-self-service-1', label: 'DE-08', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'e-separation', name: 'E-separation', subParameters: [{ id: 'e-separation-1', label: 'DE-09', marks: 30, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  { id: 'employee-one-page-profile', name: 'Employee one page profile', subParameters: [{ id: 'employee-one-page-profile-1', label: 'DE-10', marks: 20, description: 'Key HR Processes: Following key HR processes are system enabled through digital platform only.', guidelines: COMMON_GUIDELINES, auditorDetails: COMMON_AUDITOR_DETAILS, subSubParameters: [] }] },
  {
    id: 'digital-dashboards',
    name: 'Digital Dashboards',
    subParameters: [{
      id: 'digital-dashboards-1',
      label: 'DE-11',
      marks: 50,
      description: 'Covering key HR metrics / parameters \n- Dashboard to include - Trend Analysis & various slicing & bucketing features\n- Predictive System Highlighting Anomaly for timely Interventions',
      guidelines: '- Guidelines :- Digital Dashboard including key parameters to seen in system with proof of real time correct data\n- Dashboard reports from quarterly instances to be seen as evidence as well.',
      auditorDetails: 'Evidence of system based realtime dashboard with key HR parameters tracked on periodical basis',
      subSubParameters: [],
    }],
  },
];

const formatResponsibility = (val) => val ? val.split('/').map((s) => s.trim()).filter(Boolean).join(' / ') : '';

const isMonthCompleteValue = (value) => {
  if (value === null || value === undefined) return false;
  const raw = String(value).trim();
  if (!raw) return false;
  const lower = raw.toLowerCase();
  if (['-', 'na', 'n/a', 'no', '0', 'false'].includes(lower)) return false;
  return true;
};

const computeCompletionFromMonths = (values) => {
  const allDone = MONTH_COLS.every(({ key }) => isMonthCompleteValue(values?.[key]));
  return allDone ? 100 : 0;
};

const AttachmentPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  path: PropTypes.string,
  month_key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
});

const MonthWiseUploadTable = ({ values, onChange, readOnly, attachmentsByMonth, generalAttachments, onUploadMonth, onDeleteAttachment, onDownloadAttachment }) => (
  <Table bordered responsive size="sm" className="mb-3 align-middle">
    <thead className="table-light">
      <tr>
        <th style={{ width: 120 }}>Month</th>
        <th>Remark</th>
        <th style={{ width: 320 }}>Document Upload / Evidence</th>
      </tr>
    </thead>
    <tbody>
      {MONTH_COLS.map(({ key, label }) => {
        const list = Array.isArray(attachmentsByMonth?.[key]) ? attachmentsByMonth[key] : [];
        return (
          <tr key={key}>
            <td className="fw-semibold">{label}</td>
            <td>
              {readOnly ? (
                values?.[key] || <span className="text-muted">-</span>
              ) : (
                <Form.Control
                  type="text"
                  name={key}
                  value={values?.[key] || ''}
                  onChange={onChange}
                  size="sm"
                  placeholder={`Enter value for ${label}`}
                  disabled={readOnly}
                />
              )}
            </td>
            <td>
              <MonthAttachmentCell
                monthKey={key}
                monthLabel={label}
                attachments={list}
                disabled={readOnly}
                onUploadMonth={onUploadMonth}
                onDeleteAttachment={onDeleteAttachment}
                onDownloadAttachment={onDownloadAttachment}
              />
            </td>
          </tr>
        );
      })}
      {Array.isArray(generalAttachments) && generalAttachments.length > 0 && (
        <tr>
          <td className="fw-semibold">Other</td>
          <td><span className="text-muted">-</span></td>
          <td>
            <MonthAttachmentCell
              monthKey=""
              monthLabel="Other"
              attachments={generalAttachments}
              disabled
              onUploadMonth={() => {}}
              onDeleteAttachment={() => {}}
              onDownloadAttachment={onDownloadAttachment}
            />
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);

MonthWiseUploadTable.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  attachmentsByMonth: PropTypes.object,
  generalAttachments: PropTypes.arrayOf(AttachmentPropType),
  onUploadMonth: PropTypes.func.isRequired,
  onDeleteAttachment: PropTypes.func.isRequired,
  onDownloadAttachment: PropTypes.func.isRequired
};

const MONTH_ACCEPT = [
  'image/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
].join(',');

const MAX_MONTH_UPLOAD_MB = 10;
const MAX_MONTH_UPLOAD_BYTES = MAX_MONTH_UPLOAD_MB * 1024 * 1024;

const MonthAttachmentCell = ({ monthKey, monthLabel, attachments = [], disabled, onUploadMonth, onDeleteAttachment, onDownloadAttachment }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handlePick = () => inputRef.current?.click();

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.find((f) => f.size > MAX_MONTH_UPLOAD_BYTES);
    if (oversized) {
      setError(`File too large. Max ${MAX_MONTH_UPLOAD_MB} MB allowed per file.`);
      e.target.value = '';
      return;
    }
    setError('');
    setUploading(true);
    try {
      await onUploadMonth(monthKey, files);
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message || err?.data?.message || 'Upload failed. Please try again.';
      setError(message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      {attachments.length > 0 && (
        <div className="d-flex flex-column gap-1 mb-2">
          {attachments.map((a, idx) => (
            <div key={a.id || `${a.path}-${idx}`} className="d-flex align-items-center gap-2 flex-wrap">
              <span style={{ fontSize: 12 }} className="text-truncate" title={a.name || a.path}>
                {a.name || a.path}
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                className="py-0 px-2"
                onClick={() => onDownloadAttachment(a)}
                title="Download"
              >
                Download
              </Button>
              {!disabled && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="py-0 px-2"
                  onClick={() => onDeleteAttachment(a)}
                  title="Remove"
                >
                  Delete
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <Button variant="outline-secondary" size="sm" className="py-0 px-2" onClick={handlePick} disabled={uploading}>
          {uploading ? 'Uploading…' : `Attach (${monthLabel})`}
        </Button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={MONTH_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={onFilesPicked}
      />
      {error && <div style={{ fontSize: 12, color: 'var(--bs-danger)', marginTop: 4 }}>{error}</div>}
    </div>
  );
};

MonthAttachmentCell.propTypes = {
  monthKey: PropTypes.string,
  monthLabel: PropTypes.string,
  attachments: PropTypes.arrayOf(AttachmentPropType),
  disabled: PropTypes.bool,
  onUploadMonth: PropTypes.func.isRequired,
  onDeleteAttachment: PropTypes.func.isRequired,
  onDownloadAttachment: PropTypes.func.isRequired
};

const ResponsibilityField = ({ value, onSave, readOnly }) => {
  const admin = isAdmin();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      const message =
        typeof e === 'string'
          ? e
          : e?.message || e?.data?.message || 'Failed to save responsibility.';
      setError(message);
      setEditing(true);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(value || ''); setEditing(false); setError(''); };
  const display = formatResponsibility(value);

  if (!admin || readOnly) {
    return (
      <div className="mb-3">
        <strong className="hr-responsibility">Responsibility: </strong>
        <span className="hr-responsibility">{display || <em className="text-muted">Not set</em>}</span>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <strong className="hr-responsibility">Responsibility: </strong>
      {editing ? (
        <span className="d-inline-flex align-items-center gap-1 ms-1 flex-wrap">
          <Form.Control size="sm" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Name1 / Name2" className="hr-inline-input" autoFocus />
          <Button variant="success" size="sm" className="py-0 px-1" onClick={confirm} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <FiCheck size={13} />}
          </Button>
          <Button variant="outline-secondary" size="sm" className="py-0 px-1" onClick={cancel} disabled={saving}>
            <FiX size={13} />
          </Button>
          {error && <small className="text-danger ms-2">{error}</small>}
        </span>
      ) : (
        <span className="hr-responsibility">
          {display || <em className="text-muted">Not set</em>}
          <Button variant="link" size="sm" className="p-0 ms-2 d-inline-flex align-items-center gap-1" onClick={() => setEditing(true)} title="Edit responsibility">
            <FiEdit2 size={13} />
            <span>Edit</span>
          </Button>
        </span>
      )}
    </div>
  );
};

ResponsibilityField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

const SubParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  marks: PropTypes.number,
  description: PropTypes.string,
  guidelines: PropTypes.string,
  auditorDetails: PropTypes.string,
  subSubParameters: PropTypes.array
});

const ParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  subParameters: PropTypes.arrayOf(SubParameterPropType).isRequired
});

const SubParameterForm = ({ subParam, parameterId, departmentId, fyStartYear, readOnly }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [hasRecord, setHasRecord] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const attachmentsByMonth = useMemo(() => {
    const grouped = {};
    (attachments || []).forEach((a) => {
      const key = String(a?.month_key || '');
      if (!key) return;
      grouped[key] = grouped[key] || [];
      grouped[key].push(a);
    });
    return grouped;
  }, [attachments]);

  const generalAttachments = useMemo(
    () => (attachments || []).filter((a) => !String(a?.month_key || '').trim()),
    [attachments],
  );

  const fetchRecord = useCallback(async () => {
    if (!departmentId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await hrDigitalEnablementAPI.getRecord(departmentId, parameterId, subParam.id, fyStartYear);
      if (res?.data) {
        const d = res.data;
        setForm({
          actual_start_date: d.actual_start_date || '',
          actual_end_date: d.actual_end_date || '',
          completion_pct: d.completion_pct !== null && d.completion_pct !== undefined ? String(d.completion_pct) : '',
          responsibility: d.responsibility || '',
          nov_25: d.nov_25 || '', dec_25: d.dec_25 || '', jan_26: d.jan_26 || '',
          feb_26: d.feb_26 || '', mar_26: d.mar_26 || '', apr_26: d.apr_26 || '',
          may_26: d.may_26 || '', jun_26: d.jun_26 || '', jul_26: d.jul_26 || '',
          aug_26: d.aug_26 || '', sep_26: d.sep_26 || '', oct_26: d.oct_26 || '',
        });
        setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
        setHasRecord(true);
      } else {
        setForm((prev) => ({ ...prev, responsibility: '' }));
        setAttachments([]);
        setHasRecord(false);
      }
    } catch {
      setForm((prev) => ({ ...prev, responsibility: '' }));
      setAttachments([]);
      setHasRecord(false);
    } finally { setLoading(false); }
  }, [departmentId, parameterId, subParam.id, fyStartYear]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const formRef = React.useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  const saveResponsibility = async (val) => {
    const f = formRef.current;
    try {
      await hrDigitalEnablementAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id,
        actual_start_date: f.actual_start_date || null, actual_end_date: f.actual_end_date || null,
        completion_pct: computeCompletionFromMonths(f), responsibility: val || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: f[key] || null }), {}),
      });
      setForm((prev) => ({ ...prev, responsibility: val }));
      setHasRecord(true);
      notifyHrDataChanged();
    } catch (e) {
      console.error('saveResponsibility failed:', e);
      throw e;
    }
  };

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleChangeWithDerivedCompletion = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (MONTH_COLS.some((m) => m.key === name)) {
        next.completion_pct = String(computeCompletionFromMonths(next));
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    setAlert(null);
    try {
      await hrDigitalEnablementAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id,
        actual_start_date: form.actual_start_date || null, actual_end_date: form.actual_end_date || null,
        completion_pct: computeCompletionFromMonths(form), responsibility: form.responsibility || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: form[key] || null }), {}),
      });
      setAlert({ type: 'success', message: 'Saved successfully.' });
      setHasRecord(true);
      setEditMode(false);
      notifyHrDataChanged();
    } catch (e) {
      const message =
        typeof e === 'string'
          ? e
          : e?.message || e?.data?.message || 'Failed to save. Please try again.';
      setAlert({ type: 'danger', message });
    } finally { setSaving(false); }
  };

  const handleCancel = () => { fetchRecord(); setEditMode(false); setAlert(null); };
  const displayVal = (val) => val || <span className="text-muted">-</span>;

  const handleUploadMonth = async (monthKey, files) => {
    if (readOnly) return;
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    fd.append('department_id', departmentId);
    fd.append('year_start', String(fyStartYear));
    fd.append('parameter_id', parameterId);
    fd.append('sub_parameter_id', subParam.id);
    fd.append('month_key', String(monthKey));
    const res = await hrDigitalEnablementAPI.uploadAttachment(fd);
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    setHasRecord(true);
    notifyHrDataChanged();
  };

  const handleDeleteAttachment = async (attachmentIndex) => {
    if (readOnly) return;
    const res = await hrDigitalEnablementAPI.deleteAttachment({ department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id, attachment_index: attachmentIndex });
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    notifyHrDataChanged();
  };

  const handleDeleteAttachmentById = async (attachment) => {
    if (readOnly) return;
    if (!attachment?.id) {
      // Fallback: use index if older attachment has no id
      const idx = attachments.findIndex((a) => (a?.path && a.path === attachment?.path) && (a?.name === attachment?.name));
      if (idx >= 0) return handleDeleteAttachment(idx);
      return;
    }
    const res = await hrDigitalEnablementAPI.deleteAttachment({ department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id, attachment_id: String(attachment.id) });
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    notifyHrDataChanged();
  };

  const handleDownload = (attachmentIndex) => {
    hrDigitalEnablementAPI.downloadAttachment(departmentId, parameterId, subParam.id, attachmentIndex, fyStartYear).then((blob) => {
      const url = globalThis.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = attachments[attachmentIndex]?.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    });
  };

  const handleDownloadById = (attachment) => {
    if (!attachment?.id) {
      const idx = attachments.findIndex((a) => (a?.path && a.path === attachment?.path) && (a?.name === attachment?.name));
      if (idx >= 0) return handleDownload(idx);
      return;
    }
    hrDigitalEnablementAPI.downloadAttachmentById(departmentId, parameterId, subParam.id, String(attachment.id), fyStartYear).then((blob) => {
      const url = globalThis.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment?.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    });
  };

  const formFields = (
    <>
      <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
      <Row className="g-3 mb-3">
        <Col md={4}><Form.Group><Form.Label className="hr-form-label">Actual Start Date</Form.Label><Form.Control type="date" name="actual_start_date" value={form.actual_start_date} onChange={handleChange} size="sm" disabled={readOnly} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label className="hr-form-label">Actual End Date</Form.Label><Form.Control type="date" name="actual_end_date" value={form.actual_end_date} onChange={handleChange} size="sm" disabled={readOnly} /></Form.Group></Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="hr-form-label">% Completion</Form.Label>
            <Form.Control
              type="number"
              name="completion_pct"
              value={String(computeCompletionFromMonths(form))}
              readOnly
              size="sm"
              disabled
            />
            <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
              Rule: 100% only if all months are filled; otherwise 0%.
            </div>
          </Form.Group>
        </Col>
      </Row>
      <MonthWiseUploadTable
        values={form}
        onChange={handleChangeWithDerivedCompletion}
        readOnly={readOnly}
        attachmentsByMonth={attachmentsByMonth}
        generalAttachments={generalAttachments}
        onUploadMonth={handleUploadMonth}
        onDeleteAttachment={handleDeleteAttachmentById}
        onDownloadAttachment={handleDownloadById}
      />
    </>
  );

  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        {'label' in subParam && (
          <div className="d-flex align-items-center gap-2 mb-2 w-100">
            <span className="badge bg-primary hr-label-badge">{subParam.label}</span>
            {subParam.marks && <span className="badge hr-marks-badge ms-auto">{subParam.marks} Marks</span>}
          </div>
        )}
        {loading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : !hasRecord ? (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>{saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}</Button>
          </Form>
        ) : !editMode ? (
          <>
            <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
            <Row className="g-3 mb-3">
              <Col md={4}><div className="hr-detail-row"><strong>Actual Start Date:</strong> {displayVal(form.actual_start_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row"><strong>Actual End Date:</strong> {displayVal(form.actual_end_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row"><strong>% Completion:</strong> <span className={`text-${getCompletionColor(form.completion_pct || 0)}`}>{formatCompletionValue(form.completion_pct)}</span></div></Col>
            </Row>
            <MonthWiseUploadTable
              values={form}
              onChange={() => {}}
              readOnly
              attachmentsByMonth={attachmentsByMonth}
              generalAttachments={generalAttachments}
              onUploadMonth={() => {}}
              onDeleteAttachment={() => {}}
              onDownloadAttachment={handleDownloadById}
            />
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            {!readOnly && (
              <Button variant="outline-primary" size="sm" onClick={() => { setAlert(null); setEditMode(true); }}><FiEdit2 size={13} className="me-1" />Edit</Button>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            <div className="d-flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>{saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}</Button>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleCancel} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
  };

const computeParameterCompletion = (param, recordMap) => {
  const values = param.subParameters
    .map((sp) => recordMap[sp.id])
    .filter((rec) => rec && rec.completion_pct !== null && rec.completion_pct !== undefined)
    .map((rec) => parseFloat(rec.completion_pct));

  if (values.length === 0) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
};

const ParameterTile = ({ param, departmentId, fyStartYear, onClick }) => {
  const [responsibility, setResponsibility] = useState('');
  const [completionPct, setCompletionPct] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleHrDataChanged = () => setRefreshKey((value) => value + 1);
    globalThis.addEventListener('hrDataChanged', handleHrDataChanged);
    return () => globalThis.removeEventListener('hrDataChanged', handleHrDataChanged);
  }, []);

  useEffect(() => {
    if (!departmentId || param.subParameters.length === 0) return;
    hrDigitalEnablementAPI.getParameterRecords(departmentId, param.id, fyStartYear)
      .then((res) => {
        const records = res?.data || [];
        if (records.length === 0) {
          setResponsibility('');
          setCompletionPct(0);
          return;
        }
        setResponsibility([...new Set(records.map((r) => r.responsibility).filter(Boolean))].join(' / '));
        const recordMap = {};
        records.forEach((r) => { recordMap[r.sub_parameter_id] = r; });
        setCompletionPct(computeParameterCompletion(param, recordMap));
      })
      .catch(() => {
        setResponsibility('');
        setCompletionPct(0);
      });
  }, [departmentId, param, refreshKey, fyStartYear]);

  const normalizedCompletionPct = normalizeCompletionValue(completionPct);
  const pctColor = getCompletionColor(normalizedCompletionPct);

  return (
    <Card className="shadow-sm h-100 hr-tile-card" onClick={onClick}>
      {responsibility && <div className="hr-tile-responsibility">{responsibility}</div>}
      <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
        <h6 className="mb-0 text-center">{param.name}</h6>
        <div className="mt-3 w-100">
          <div className="d-flex justify-content-between mb-1 hr-tile-meta">
            <span className="text-muted">Completion</span>
            <span className={`hr-tile-percent text-${pctColor}`}>{formatCompletionValue(normalizedCompletionPct)}</span>
          </div>
          <div className="hr-progress" style={{ '--hr-progress-width': `${normalizedCompletionPct}%` }}>
            <div className={`hr-progress-fill hr-progress-${pctColor}`} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

ParameterTile.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};

const ParameterDetail = ({ param, departmentId, onBack, onBackToMain, fyStartYear, readOnly }) => (
  <Container fluid className="dashboard-page py-4">
    <div className="d-flex align-items-center gap-2 mb-4">
      <Button variant="outline-secondary" size="sm" onClick={onBack}><FiArrowLeft size={14} className="me-1" />Back</Button>
      <Button variant="outline-primary" size="sm" onClick={onBackToMain}><FiArrowLeft size={14} className="me-1" />HR Main Dashboard</Button>
      <h5 className="mb-0">{param.name}</h5>
    </div>
    {param.subParameters.map((sp) => (
      <SubParameterForm key={sp.id} subParam={sp} parameterId={param.id} departmentId={departmentId} fyStartYear={fyStartYear} readOnly={readOnly} />
    ))}
  </Container>
);

ParameterDetail.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func.isRequired,
  onBackToMain: PropTypes.func.isRequired,
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

const SectionChooser = ({
  keyHrTotalMarks,
  digitalDashboardsTotalMarks,
  recordsLoading,
  keyHrResponsibles,
  digitalDashResponsibles,
  onSelectKeyHr,
  onSelectDigitalDashboards
}) => {
  const keyHrResponsibleContent = (() => {
    if (recordsLoading) return <span className="text-muted small">Loading…</span>;
    if (keyHrResponsibles.length) {
      return (
        <div className="d-flex flex-wrap gap-1">
          {keyHrResponsibles.map((r) => (
            <span key={r} className="badge bg-light text-dark fw-normal">{r}</span>
          ))}
        </div>
      );
    }
    return <span className="text-muted small">-</span>;
  })();

  const digitalDashResponsibleContent = (() => {
    if (recordsLoading) return <span className="text-muted small">Loading…</span>;
    if (digitalDashResponsibles.length) {
      return (
        <div className="d-flex flex-wrap gap-1">
          {digitalDashResponsibles.map((r) => (
            <span key={r} className="badge bg-light text-dark fw-normal">{r}</span>
          ))}
        </div>
      );
    }
    return <span className="text-muted small">-</span>;
  })();

  return (
    <Row className="g-3">
    <Col lg={6}>
      <Card className="shadow-sm h-100 hr-tile-card" onClick={onSelectKeyHr}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-1">Key HR Processes</h5>
              <div className="text-muted small">10 modules (DE-01 to DE-10)</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge hr-marks-badge">{keyHrTotalMarks} Marks</span>
              <span className="badge bg-primary">Open</span>
            </div>
          </div>
          <div className="mt-3 text-muted small">
            Select this to update month-wise remarks and evidence for all 10 HR modules.
          </div>
          <div className="mt-2">
            <div className="text-muted small mb-1">Responsible</div>
            {keyHrResponsibleContent}
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className="shadow-sm h-100 hr-tile-card" onClick={onSelectDigitalDashboards}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-1">Digital Dashboards</h5>
              <div className="text-muted small">DE-11 (50 Marks)</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge hr-marks-badge">{digitalDashboardsTotalMarks} Marks</span>
              <span className="badge bg-success">Open</span>
            </div>
          </div>
          <div className="mt-3 text-muted small">
            Trend analysis, slicing/bucketing, and anomaly highlighting for timely interventions.
          </div>
          <div className="mt-2">
            <div className="text-muted small mb-1">Responsible</div>
            {digitalDashResponsibleContent}
          </div>
        </Card.Body>
      </Card>
    </Col>
  </Row>
  );
};

SectionChooser.propTypes = {
  keyHrTotalMarks: PropTypes.number.isRequired,
  digitalDashboardsTotalMarks: PropTypes.number.isRequired,
  recordsLoading: PropTypes.bool.isRequired,
  keyHrResponsibles: PropTypes.arrayOf(PropTypes.string).isRequired,
  digitalDashResponsibles: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectKeyHr: PropTypes.func.isRequired,
  onSelectDigitalDashboards: PropTypes.func.isRequired
};

const SectionDetail = ({
  selectedSection,
  onBack,
  keyHrTotalMarks,
  digitalDashboardsTotalMarks,
  keyHrParams,
  digitalDashboardParams,
  departmentId,
  fyStartYear,
  onSelectParam
}) => {
  const params = selectedSection === 'key-hr' ? keyHrParams : digitalDashboardParams;
  const totalMarks = selectedSection === 'key-hr' ? keyHrTotalMarks : digitalDashboardsTotalMarks;
  const isKeyHr = selectedSection === 'key-hr';
  const digitalIntro = digitalDashboardParams[0]?.subParameters?.[0];

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onBack}>
          <FiArrowLeft size={14} className="me-1" />Back
        </Button>
        <h6 className="mb-0">{isKeyHr ? 'Key HR Processes' : 'Digital Dashboards'}</h6>
        <span className="badge hr-marks-badge ms-auto">Total: {totalMarks} Marks</span>
      </div>

      {isKeyHr && (
        <Card className="shadow-sm mb-3 border-0">
          <Card.Body>
            <div className="mb-3">
              <strong className="hr-section-heading">Parameter</strong>
              <p className="mb-0 hr-section-text">
                Key HR Processes: Following key HR processes are system enabled through digital platform only.
              </p>
            </div>
            <div className="mb-3">
              <strong className="hr-section-heading">Guidelines for Scoring Matrix</strong>
              <pre className="mt-1 mb-0 hr-section-pre">{COMMON_GUIDELINES}</pre>
            </div>
            <div className="mb-0">
              <strong className="hr-section-heading">Details Required by Auditors</strong>
              <pre className="mt-1 mb-0 hr-section-pre">{COMMON_AUDITOR_DETAILS}</pre>
            </div>
          </Card.Body>
        </Card>
      )}

      {!isKeyHr && digitalIntro && (
        <Card className="shadow-sm mb-3 border-0">
          <Card.Body>
            <div className="mb-3">
              <strong className="hr-section-heading">Parameter</strong>
              <p className="mb-0 hr-section-text">{digitalIntro.description}</p>
            </div>
            <div className="mb-3">
              <strong className="hr-section-heading">Guidelines for Scoring Matrix</strong>
              <pre className="mt-1 mb-0 hr-section-pre">{digitalIntro.guidelines}</pre>
            </div>
            <div className="mb-0">
              <strong className="hr-section-heading">Details Required by Auditors</strong>
              <pre className="mt-1 mb-0 hr-section-pre">{digitalIntro.auditorDetails}</pre>
            </div>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 row-cols-1 row-cols-md-2 row-cols-lg-3">
        {params.map((param) => (
          <Col key={param.id}>
            <ParameterTile param={param} departmentId={departmentId} fyStartYear={fyStartYear} onClick={() => onSelectParam(param)} />
          </Col>
        ))}
      </Row>
    </>
  );
};

SectionDetail.propTypes = {
  selectedSection: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  keyHrTotalMarks: PropTypes.number.isRequired,
  digitalDashboardsTotalMarks: PropTypes.number.isRequired,
  keyHrParams: PropTypes.arrayOf(ParameterPropType).isRequired,
  digitalDashboardParams: PropTypes.arrayOf(ParameterPropType).isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  onSelectParam: PropTypes.func.isRequired
};

const HRDigitalEnablementDashboardPage = ({ departmentId = null }) => {
  const navigate = useNavigate();
  const [fyStartYear, setFyStartYear] = useState(getStoredHrYear());
  const [selectedSection, setSelectedSection] = useState(null); // 'key-hr' | 'digital-dashboards'
  const [selectedParam, setSelectedParam] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const currentFy = getFiscalYearStartYear(new Date());
  const readOnly = !isAdmin() && fyStartYear !== currentFy;

  useEffect(() => {
    try { localStorage.setItem('hrSelectedYear', String(fyStartYear)); } catch {}
  }, [fyStartYear]);

  const goToHrMainDashboard = () => {
    localStorage.setItem('selectedProject', JSON.stringify({
      id: 'hr-main-dashboard',
      name: 'HR Main Dashboard',
      departmentId,
    }));
    globalThis.dispatchEvent(new Event('projectChanged'));
    navigate('/dashboard', { replace: true });
  };

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      if (!departmentId) {
        setAllRecords([]);
        return;
      }
      setRecordsLoading(true);
      try {
        const res = await hrDigitalEnablementAPI.getAllRecords(departmentId, fyStartYear);
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (mounted) setAllRecords(rows);
      } catch {
        if (mounted) setAllRecords([]);
      } finally {
        if (mounted) setRecordsLoading(false);
      }
    };
    loadAll();
    return () => { mounted = false; };
  }, [departmentId, fyStartYear]);

  const keyHrParams = INIT_PARAMETERS.filter((p) => p.id !== 'digital-dashboards');
  const digitalDashboardParams = INIT_PARAMETERS.filter((p) => p.id === 'digital-dashboards');

  const responsiblesForParams = useMemo(() => {
    const byParam = new Map();
    (allRecords || []).forEach((r) => {
      const pid = String(r?.parameter_id || '');
      const resp = String(r?.responsibility || '').trim();
      if (!pid || !resp) return;
      const set = byParam.get(pid) || new Set();
      resp.split('/').map((s) => s.trim()).filter(Boolean).forEach((v) => set.add(v));
      byParam.set(pid, set);
    });
    return byParam;
  }, [allRecords]);

  const keyHrResponsibles = useMemo(() => {
    const s = new Set();
    (keyHrParams || []).forEach((p) => {
      const set = responsiblesForParams.get(String(p.id));
      if (!set) return;
      Array.from(set).forEach((v) => s.add(v));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b)).slice(0, 6);
  }, [keyHrParams, responsiblesForParams]);

  const digitalDashResponsibles = useMemo(() => {
    const s = new Set();
    (digitalDashboardParams || []).forEach((p) => {
      const set = responsiblesForParams.get(String(p.id));
      if (!set) return;
      Array.from(set).forEach((v) => s.add(v));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b)).slice(0, 6);
  }, [digitalDashboardParams, responsiblesForParams]);

  const sumMarks = (params) => (params || []).reduce((sum, p) => (
    sum + (p?.subParameters || []).reduce((s, sp) => s + (Number(sp?.marks) || 0), 0)
  ), 0);
  const keyHrTotalMarks = sumMarks(keyHrParams);
  const digitalDashboardsTotalMarks = sumMarks(digitalDashboardParams);

  if (selectedParam) {
    return (
      <ParameterDetail
        param={selectedParam}
        departmentId={departmentId}
        onBack={() => setSelectedParam(null)}
        onBackToMain={goToHrMainDashboard}
        fyStartYear={fyStartYear}
        readOnly={readOnly}
      />
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <Button variant="outline-primary" size="sm" onClick={goToHrMainDashboard}>
          <FiArrowLeft size={14} className="me-1" />HR Main Dashboard
        </Button>
        <h5 className="mb-0">Digital &amp; Enablement</h5>
        <div className="ms-auto d-flex align-items-center gap-2">
          {readOnly && <span className="text-muted small">Read-only (previous year)</span>}
          <Form.Select size="sm" value={fyStartYear} onChange={(e) => setFyStartYear(Number(e.target.value))} style={{ width: 140 }}>
            {[currentFy - 1, currentFy, currentFy + 1].map((y) => (
              <option key={y} value={y}>{formatYearLabel(y)}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      {selectedSection === null ? (
        <SectionChooser
          keyHrTotalMarks={keyHrTotalMarks}
          digitalDashboardsTotalMarks={digitalDashboardsTotalMarks}
          recordsLoading={recordsLoading}
          keyHrResponsibles={keyHrResponsibles}
          digitalDashResponsibles={digitalDashResponsibles}
          onSelectKeyHr={() => setSelectedSection('key-hr')}
          onSelectDigitalDashboards={() => setSelectedSection('digital-dashboards')}
        />
      ) : (
        <SectionDetail
          selectedSection={selectedSection}
          onBack={() => setSelectedSection(null)}
          keyHrTotalMarks={keyHrTotalMarks}
          digitalDashboardsTotalMarks={digitalDashboardsTotalMarks}
          keyHrParams={keyHrParams}
          digitalDashboardParams={digitalDashboardParams}
          departmentId={departmentId}
          fyStartYear={fyStartYear}
          onSelectParam={setSelectedParam}
        />
      )}
    </Container>
  );
  };

SubParameterForm.propTypes = {
  subParam: SubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

HRDigitalEnablementDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HRDigitalEnablementDashboardPage;
